/**
 * The Odżywianie tab — nutrition stage 1 (`nutrition-requirements.md` R3, R9–R12), against the
 * approved mockup `nutrition-designs/web-nutrition.html`: the Pomiary stage card on top, the Ankiety
 * stream-and-pane split under it.
 *
 * Every decision is `model/nutritionStage`'s and `model/nutritionWeeks`'; this file holds the
 * trainer's choices (range, nutrient, weight line, week, comparison, open day, the sheet) and places
 * boxes. One skeleton gate for the whole tab: the stage, the list and the pane come off the same
 * three reads, so the tab is handed over once.
 */

import { motion } from 'motion/react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { screenMotion } from '~/shared/design-system/motion';
import { useSkeletonGate } from '~/shared/lib/useSkeletonGate';
import { EmptyState } from '~/shared/ui/EmptyState';
import { TrendMarkIcon } from '~/shared/ui/EmptyStateIcons';
import skel from '~/shared/ui/skeleton.module.css';

import { useNutrition } from '../lib/useNutrition';
import { weekBundles, type Nutrient, type NutritionSpan } from '../model/nutrition';
import { buildNutritionStage, lastEntryWords } from '../model/nutritionStage';
import {
  anyWeekLogged,
  buildWeekCompare,
  buildWeekList,
  buildWeekPane,
  nextComparePick,
} from '../model/nutritionWeeks';

import { useCardAnnounce } from './cardAnnouncer';
import detail from './DetailTab.module.css';
import { usePublishTabMeta } from './detailTabMeta';
import { NutritionCompare } from './NutritionCompare';
import { NutritionStage } from './NutritionStage';
import styles from './NutritionTab.module.css';
import { NutritionTargetDialog } from './NutritionTargetDialog';
import { NutritionWeekTable } from './NutritionWeekTable';
import { NutritionWeeks } from './NutritionWeeks';

const KEY = 'web:clientDetail.nutrition';
const PANE_SKELETON_ROWS = 7;

interface NutritionTabProps {
  readonly clientId: number;
  readonly clientName: string;
  readonly reducedMotion: boolean;
}

/**
 * Keyed on the client, so the chosen range, nutrient, week, comparison and open day are discarded
 * when „Następna ›" moves to the next one: the week and the comparison are indices into her list.
 */
export function NutritionTab({ clientId, clientName, reducedMotion }: NutritionTabProps) {
  return (
    <NutritionTabBody
      key={clientId}
      clientId={clientId}
      clientName={clientName}
      reducedMotion={reducedMotion}
    />
  );
}

function NutritionTabBody({ clientId, clientName, reducedMotion }: NutritionTabProps) {
  const { t, i18n } = useTranslation();
  const words = { t, locale: i18n.language };
  const announce = useCardAnnounce();
  const [span, setSpan] = useState<NutritionSpan>('week');
  const [nutrient, setNutrient] = useState<Nutrient>('energy');
  const [weight, setWeight] = useState(false);
  const [week, setWeek] = useState(0);
  const [compare, setCompare] = useState<readonly [number, number] | null>(null);
  const [openDay, setOpenDay] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  const compareToggle = useRef<HTMLButtonElement>(null);

  const reads = useNutrition(clientId, span);
  const gate = useSkeletonGate(reads.pending);
  const answered = !gate.waiting && !reads.failed && !reads.unavailable;

  // The stage is drawn as the span its data answers; only the presets follow the click at once.
  const stage =
    answered && reads.range !== null && reads.rangeSpan !== null
      ? buildNutritionStage(
          { target: reads.target, range: reads.range, span: reads.rangeSpan, nutrient, weight },
          words,
        )
      : null;
  const bundles = answered && reads.quarter !== null ? weekBundles(reads.quarter) : [];
  const months = buildWeekList(bundles, reads.target !== null, words);
  const nothingLogged = answered && !anyWeekLogged(bundles);
  const chosen = bundles[Math.min(week, Math.max(0, bundles.length - 1))] ?? null;
  const pane = chosen === null ? null : buildWeekPane(chosen, words);
  const pair =
    compare === null ? null : ([bundles[compare[0]], bundles[compare[1]]] as const);
  const compareView =
    pair === null || pair[0] === undefined || pair[1] === undefined
      ? null
      : buildWeekCompare(pair[0], pair[1], reads.target, words);

  const lastEntryAt = reads.range?.lastEntryAt ?? null;
  const lastEntry = answered && lastEntryAt !== null ? lastEntryWords(lastEntryAt, new Date(), words) : null;
  usePublishTabMeta(lastEntry === null ? null : t('web:clientDetail.tabMeta.nutrition', { when: lastEntry }));

  if (!gate.waiting && reads.unavailable) {
    return (
      <div className={detail.tab}>
        <section className={`${String(detail.card)} ${String(styles.unavailable)}`}>
          <EmptyState mark={<TrendMarkIcon />} title={t(`${KEY}.unavailable`)} tone="neutral" />
        </section>
      </div>
    );
  }

  return (
    <div className={`${String(detail.tab)} ${String(styles.tabBox)}`}>
      <motion.section
        className={`${String(detail.card)} ${String(detail.stageCard)}`}
        {...screenMotion('enter', { reducedMotion })}
      >
        <NutritionStage
          failed={reads.failed}
          gate={gate}
          nutrient={nutrient}
          onEditTarget={() => {
            setEditing(true);
          }}
          onNutrient={setNutrient}
          onRetry={reads.retry}
          onSpan={setSpan}
          onWeight={setWeight}
          span={span}
          view={stage}
          weight={weight}
        />
      </motion.section>

      {nothingLogged ? null : (
        <motion.div className={styles.split} {...screenMotion('enter', { reducedMotion, index: 1 })}>
          <div className={styles.weeksColumn}>
            <NutritionWeeks
              compare={compare}
              compareToggleRef={compareToggle}
              count={bundles.length}
              failed={reads.failed}
              gate={gate}
              months={months}
              onPick={(index) => {
                setCompare((current) => (current === null ? current : nextComparePick(current, index)));
              }}
              onSelect={(index) => {
                setWeek(index);
                setOpenDay(null);
              }}
              onToggleCompare={() => {
                // The mockup opens on last week against this one.
                setCompare((current) => (current === null ? [Math.min(1, bundles.length - 1), 0] : null));
              }}
              selected={week}
            />
          </div>
          <section className={`${String(detail.card)} ${String(styles.pane)}`} aria-busy={gate.waiting}>
            {gate.waiting ? (
              gate.skeleton ? (
                <span className={styles.paneWait}>
                  {Array.from({ length: PANE_SKELETON_ROWS }, (_, row) => (
                    <i key={row} className={`${String(skel.bar)} ${String(styles.skelRow)}`} />
                  ))}
                </span>
              ) : null
            ) : reads.failed ? (
              <p className={styles.paneCaption}>{t(`${KEY}.failed`)}</p>
            ) : compareView !== null ? (
              <div className={`${String(styles.paneBody)} ${gate.reveal}`}>
                <NutritionCompare
                  onClose={() => {
                    // The pane's ✕ leaves with the comparison; without this focus falls to <body>.
                    compareToggle.current?.focus();
                    setCompare(null);
                  }}
                  onSwap={() => {
                    setCompare((current) => (current === null ? null : [current[1], current[0]]));
                  }}
                  view={compareView}
                />
              </div>
            ) : pane === null ? null : (
              <div className={`${String(styles.paneBody)} ${gate.reveal}`}>
                <NutritionWeekTable
                  clientId={clientId}
                  onToggleDay={(date) => {
                    setOpenDay((current) => (current === date ? null : date));
                  }}
                  openDay={openDay}
                  pane={pane}
                />
              </div>
            )}
          </section>
        </motion.div>
      )}

      <NutritionTargetDialog
        announce={announce}
        clientId={clientId}
        clientName={clientName}
        onClose={() => {
          setEditing(false);
        }}
        open={editing}
        target={reads.target}
      />
    </div>
  );
}
